/** Shadcn-style, portalled Radix popover primitives. */
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { type ComponentProps, type ReactElement, useRef } from "react";
import { useTranslation } from "react-i18next";

import { directionFor } from "@/i18n";
import { cn } from "@/shared/lib/format";

/** Controlled popover root. */
export const Popover = PopoverPrimitive.Root;
/** Compose a trigger with an existing button. */
export const PopoverTrigger = PopoverPrimitive.Trigger;

/** Collision-aware panel with focus return, Escape and outside-click dismissal. */
export function PopoverContent({
  className,
  align = "start",
  sideOffset = 6,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Content>): ReactElement {
  const { i18n } = useTranslation();
  const content = useRef<HTMLDivElement>(null);
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={content}
        dir={directionFor(i18n.resolvedLanguage ?? "en")}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        onOpenAutoFocus={(event) => {
          if (window.matchMedia("(pointer: coarse)").matches) {
            event.preventDefault();
            content.current?.focus();
          }
        }}
        className={cn(
          "z-50 w-80 max-w-[calc(100vw-1.5rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain rounded-lg border bg-background text-foreground shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}
